import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { clauseTypesApi } from "../../api/clauseTypeApi";
import type { ClauseType } from "../../types/clauseType.types";
import type { RootState, AppDispatch } from "../../app/store";
import { toast } from "../../components/common/toast";
import { getApiErrorMessage } from "../../utils/apiError";
import { reportDeleteFailure } from "../../utils/deleteFailure";
import i18n from "../../i18n";
import { getCurrentUser } from "../../auth/auth.service";

interface ClauseTypesState {
  items: ClauseType[];
  isLoading: boolean;
  isAdding: boolean;
  isEditing: boolean;
  isDeleting: boolean;
  hasMore: boolean;
  pageIndex: number;
  totalCount: number;
  error: string | null;
}

const initialState: ClauseTypesState = {
  items: [],
  isLoading: false,
  isAdding: false,
  isEditing: false,
  isDeleting: false,
  hasMore: true,
  pageIndex: 0,
  totalCount: 0,
  error: null,
};

const clauseTypesSlice = createSlice({
  name: "clauseTypes",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setAdding: (state, action: PayloadAction<boolean>) => {
      state.isAdding = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => {
      state.isEditing = action.payload;
    },
    setDeleting: (state, action: PayloadAction<boolean>) => {
      state.isDeleting = action.payload;
    },
    setClauseTypes: (state, action: PayloadAction<ClauseType[]>) => {
      state.items = action.payload;
    },
    appendClauseTypes: (state, action: PayloadAction<ClauseType[]>) => {
      state.items = [...state.items, ...action.payload];
    },
    addSingleClauseTypeLocally: (
      state,
      action: PayloadAction<ClauseType>,
    ) => {
      state.items.unshift(action.payload);
      state.totalCount += 1;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPageIndex: (state, action: PayloadAction<number>) => {
      state.pageIndex = action.payload;
    },
    resetClauseTypeList: (state) => {
      state.items = [];
      state.hasMore = true;
      state.pageIndex = 0;
      state.totalCount = 0;
    },
    updateSingleClauseType: (
      state,
      action: PayloadAction<Partial<ClauseType> & { id: string }>,
    ) => {
      const index = state.items.findIndex((ct) => ct.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
    removeSingleClauseType: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((ct) => ct.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
  },
});

export const {
  setLoading,
  setAdding,
  setEditing,
  setDeleting,
  setClauseTypes,
  appendClauseTypes,
  addSingleClauseTypeLocally,
  setTotalCount,
  setHasMore,
  setPageIndex,
  resetClauseTypeList,
  updateSingleClauseType,
  removeSingleClauseType,
} = clauseTypesSlice.actions;

interface FetchClauseTypesPayload {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  sortAscending?: boolean;
  groupIds?: string[];
  ids?: string[];
  searchText?: string;
  includeDeleted?: boolean;
  /** Dropdowns pass true so deactivated entries can't be picked;
   *  the listing filter passes null for "show both". */
  isActive?: boolean | null;
}

export const fetchClauseTypes = (payload: FetchClauseTypesPayload = {}) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().clauseTypes;
    const userData = await getCurrentUser();

    const incomingPageIndex = payload.pageIndex ?? state.pageIndex;

    if (state.isLoading || (!state.hasMore && incomingPageIndex !== 0)) return;

    try {
      dispatch(setLoading(true));

      const requestBody = {
        pageSize: payload.pageSize || 10,
        pageIndex: incomingPageIndex,
        orderBy: payload.orderBy || "string",
        // This endpoint reads the flag as "ascending" despite its name — a
        // quirk every dropdown in the app relies on, so it is matched here
        // rather than changed there.
        sortDecending: payload.sortAscending ?? false,
        groupIds: payload.groupIds || [userData?.companyId],
        ids: payload.ids,
        searchText: payload.searchText || "",
        includeDeleted: payload.includeDeleted || false,
        ...(payload.isActive !== undefined && { isActive: payload.isActive }),
      };

      const response = await clauseTypesApi.searchClauseTypes(requestBody);
      const fetchedData = response.data || [];
      const totalCount = response.count || response.totalCount || 0;

      dispatch(setTotalCount(totalCount));

      const currentLoadedCount =
        incomingPageIndex === 0
          ? fetchedData.length
          : state.items.length + fetchedData.length;

      if (currentLoadedCount >= totalCount || fetchedData.length < 10) {
        dispatch(setHasMore(false));
      } else {
        dispatch(setHasMore(true));
      }

      if (incomingPageIndex === 0) {
        dispatch(setClauseTypes(fetchedData));
        dispatch(setPageIndex(1));
      } else {
        dispatch(appendClauseTypes(fetchedData));
        dispatch(setPageIndex(incomingPageIndex + 1));
      }
    } catch (error) {
      console.error("Fetch Clause Types error:", error);
      toast.error(
        i18n.t("clauseTypes:clauseType.messages.fetchError", {
          defaultValue: "Failed to fetch clause types",
        }),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const addClauseType = (data: { clauseType: string }) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setAdding(true));

      const payload = {
        clauseName: data.clauseType,
      };

      const res = await clauseTypesApi.createClauseType(payload);

      const createdId = res?.data?.id || res?.data || res?.id || res;

      const newClauseType: ClauseType = {
        clauseName: data.clauseType,
        id: typeof createdId === "string" ? createdId : createdId?.id,
        groupId: res?.data?.groupId || res?.groupId || "",
      };

      dispatch(addSingleClauseTypeLocally(newClauseType));

      toast.success(
        i18n.t("clauseTypes:clauseType.messages.addSuccess", {
          defaultValue: "Clause Type created successfully",
        }),
      );
      return newClauseType;
    } catch (error) {
      console.error("Create Clause Type error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("clauseTypes:clauseType.messages.addError", {
          defaultValue: "Failed to create clause type",
        })));
      throw error;
    } finally {
      dispatch(setAdding(false));
    }
  };
};

export const editClauseType = (data: {
  id: string;
  clauseType: string;
}) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setEditing(true));

      const payload = {
        id: data.id,
        clauseName: data.clauseType,
      };

      const res = await clauseTypesApi.updateClauseType(payload);
      const updatedType = res?.data || payload;

      dispatch(updateSingleClauseType(updatedType));

      toast.success(
        i18n.t("clauseTypes:clauseType.messages.editSuccess", {
          defaultValue: "Clause Type updated successfully",
        }),
      );
      return updatedType;
    } catch (error) {
      console.error("Edit Clause Type error:", error);
      toast.error(getApiErrorMessage(error, i18n.t("clauseTypes:clauseType.messages.editError", {
          defaultValue: "Failed to update clause type",
        })));
      throw error;
    } finally {
      dispatch(setEditing(false));
    }
  };
};

export const removeClauseType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(setDeleting(true));

      dispatch(removeSingleClauseType(id));

      await clauseTypesApi.removeClauseType(id);

      toast.success(
        i18n.t("clauseTypes:clauseType.messages.removeSuccess", {
          defaultValue: "Clause Type removed successfully",
        }),
      );
    } catch (error) {
      console.error("Remove Clause Type error:", error);
      // The API refuses deletion while clauses still use the
      // record and says which — show that reason, not a generic failure.
      const fallback = i18n.t("clauseTypes:clauseType.messages.removeError", {
        defaultValue: "Failed to remove clause type",
      });
      reportDeleteFailure(error, fallback);
      throw error;
    } finally {
      dispatch(setDeleting(false));
    }
  };
};

export default clauseTypesSlice.reducer;

export const activateClauseType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    // Optimistic, then rolled back on failure — same shape as the
    // department/business-unit toggles.
    dispatch(updateSingleClauseType({ id, isActive: true } as any));

    try {
      await clauseTypesApi.activateClauseType(id);

      toast.success(
        i18n.t("clauseTypes:clauseType.messages.activateSuccess", {
          defaultValue: "Clause Type activated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleClauseType({ id, isActive: false } as any));
      console.error("Activate Clause Type error:", error);
      toast.error(
        i18n.t("clauseTypes:clauseType.messages.activateError", {
          defaultValue: "Failed to activate clause type",
        }),
      );
    }
  };
};

export const deactivateClauseType = (id: string) => {
  return async (dispatch: AppDispatch) => {
    dispatch(updateSingleClauseType({ id, isActive: false } as any));

    try {
      await clauseTypesApi.deactivateClauseType(id);

      toast.success(
        i18n.t("clauseTypes:clauseType.messages.deactivateSuccess", {
          defaultValue: "Clause Type deactivated",
        }),
      );
    } catch (error) {
      dispatch(updateSingleClauseType({ id, isActive: true } as any));
      console.error("Deactivate Clause Type error:", error);
      toast.error(
        i18n.t("clauseTypes:clauseType.messages.deactivateError", {
          defaultValue: "Failed to deactivate clause type",
        }),
      );
    }
  };
};
